import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, Typography, Grid, Paper, TextField, Button, 
  FormControl, InputLabel, Select, MenuItem, CircularProgress 
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import LogViewer from '../../../components/Super-Admin/audit-security/LogViewer';
import { getAuditLogs } from '../../../services/auditService';
import { fetchJson } from '../../../utils/fetchJson';

export default function ActivityLogsPage() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
    actionType: '',
    schoolId: '',
    userType: ''
  });
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchSchools();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        from: filters.dateFrom?.toISOString(),
        to: filters.dateTo?.toISOString(),
        action: filters.actionType,
        schoolId: filters.schoolId,
        userType: filters.userType
      };
      
      const response = await getAuditLogs(params);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchools = async () => {
    try {
      const data = await fetchJson('/api/schools', { method: 'GET' });
      setSchools(Array.isArray(data?.schools) ? data.schools : Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch schools:', error);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      dateFrom: null,
      dateTo: null,
      actionType: '',
      schoolId: '',
      userType: ''
    });
    fetchLogs();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" gutterBottom>
        {t('activityLogs.title', 'System Activity Logs')}
      </Typography>
      
      <Paper sx={{ p: 3, mb: 4 }} elevation={1}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t('activityLogs.actionType', 'Action Type')}
              value={filters.actionType}
              onChange={(e) => handleFilterChange('actionType', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('activityLogs.school', 'School')}</InputLabel>
              <Select
                value={filters.schoolId}
                onChange={(e) => handleFilterChange('schoolId', e.target.value)}
                label={t('activityLogs.school', 'School')}
              >
                <MenuItem value="">{t('activityLogs.allSchools', 'All Schools')}</MenuItem>
                {schools.map(school => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('activityLogs.userType', 'User Type')}</InputLabel>
              <Select
                value={filters.userType}
                onChange={(e) => handleFilterChange('userType', e.target.value)}
                label={t('activityLogs.userType', 'User Type')}
              >
                <MenuItem value="">{t('activityLogs.allTypes', 'All Types')}</MenuItem>
                <MenuItem value="SUPER_ADMIN">{t('activityLogs.superAdmin', 'Super Admin')}</MenuItem>
                <MenuItem value="SCHOOL_ADMIN">{t('activityLogs.schoolAdmin', 'School Admin')}</MenuItem>
                <MenuItem value="TEACHER">{t('activityLogs.teacher', 'Teacher')}</MenuItem>
                <MenuItem value="PARENT">{t('activityLogs.parent', 'Parent')}</MenuItem>
                <MenuItem value="STUDENT">{t('activityLogs.student', 'Student')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<FilterListIcon />}
              onClick={fetchLogs}
              fullWidth
            >
              {t('activityLogs.applyFilters', 'Apply Filters')}
            </Button>
            <Button 
              variant="outlined"
              onClick={handleResetFilters}
              fullWidth
            >
              {t('activityLogs.reset', 'Reset')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <LogViewer logs={logs} />
      )}
    </Box>
  );
}